import type { SiteConfigFn } from '@wix/thunderbolt-vsm-api-types'
import type { TpaCommonsSiteConfig } from '../types'
import type { TPAPublicClientSpecEntry } from '@wix/thunderbolt-ssr-api'
import { TPA_COMPONENTS } from '@wix/thunderbolt-commons'

const tpaCommonsSiteConfig: SiteConfigFn<TpaCommonsSiteConfig> = ({ api, funcLib }) => {
	const hasPlatformOnSite = api.features.isActive('platform')
	const hasTpaWorkerOnSite = api.features.isActive('tpaWorkerFeature')

	// Check if TPA components exist in renderHints
	const componentsCountHints = api.rendererModel.getSiteMetaData()?.renderHints?.componentsCount ?? {}
	const hasTPA = Object.keys(componentsCountHints).some(
		(compType) => TPA_COMPONENTS[compType as keyof typeof TPA_COMPONENTS]
	)

	const shouldLoadSiteFeature = hasTPA || hasPlatformOnSite || hasTpaWorkerOnSite

	if (!shouldLoadSiteFeature) {
		return {
			shouldLoadSiteFeature: false,
			shouldLoadSiteConfig: false,
			siteConfig: null,
		}
	}

	// Extract widget URL overrides
	const requestUrl = api.originalRequest.getUrl() ?? ''
	const widgetOverrideParam = funcLib.getQueryParamValue(requestUrl, 'tpaWidgetUrlOverride', { toLowerCase: false })
	const widgetsOverrides = funcLib.parseCommaSeparatedObject(widgetOverrideParam ?? undefined)

	// Get TPA debug params
	const debugApp = funcLib.getQueryParamValue(requestUrl, 'debugApp', { toLowerCase: false })
	const petriOvr = funcLib.getQueryParamValue(requestUrl, 'petri_ovr', { toLowerCase: false })
	const tpaDebugParams = {
		debugApp: debugApp ?? null,
		petri_ovr: petriOvr ?? null,
	}

	// Get form factor and calculate isMobileView
	const formFactor = api.env.getFormFactor()
	const isMobileView = formFactor === 'mobile'

	// Get clientSpecMap
	const clientSpecMap = api.rendererModel.getClientSpecMap()

	// Filter apps with widgets
	const appsWithWidgets: Record<string, TPAPublicClientSpecEntry> = {}
	for (const [key, csm] of Object.entries(clientSpecMap)) {
		if (
			(csm as TPAPublicClientSpecEntry).widgets &&
			Object.keys((csm as TPAPublicClientSpecEntry).widgets).length > 0
		) {
			appsWithWidgets[key] = csm as TPAPublicClientSpecEntry
		}
	}

	const extractMobileUrl = (widget: any) => {
		const isInMobileDevMode = false
		if (widgetsOverrides[widget.widgetId]) {
			return widgetsOverrides[widget.widgetId]
		}
		if (widget.mobilePublished || isInMobileDevMode) {
			return widget.mobileUrl
		}
		return null
	}

	// Build widgetsClientSpecMapData
	const widgetsClientSpecMapData: TpaCommonsSiteConfig['widgetsClientSpecMapData'] = {}

	for (const app of Object.values(appsWithWidgets)) {
		const widgets = app.widgets || {}

		for (const [widgetKey, widget] of Object.entries(widgets)) {
			// Extract widget URL (with override)
			const widgetUrl =
				widget.widgetId && widgetsOverrides[widget.widgetId] ? widgetsOverrides[widget.widgetId] : widget.widgetUrl

			const mobileUrl = extractMobileUrl(widget)

			// Calculate allowScrolling
			const fullPage = widget.appPage?.fullPage ?? false
			const fullPageDesktopOnly = widget.componentFields?.fullPageDesktopOnly ?? false
			const allowScrolling = fullPage || (fullPageDesktopOnly && !isMobileView)

			widgetsClientSpecMapData[widgetKey] = {
				widgetUrl: widgetUrl as string,
				mobileUrl: mobileUrl as string | undefined,
				tpaWidgetId: widget.tpaWidgetId as string,
				appPage: widget.appPage || {},
				applicationId: app.applicationId,
				appDefinitionName: app.appDefinitionName,
				appDefinitionId: app.appDefinitionId,
				isWixTPA: app.isWixTPA,
				allowScrolling,
			}
		}
	}

	// Build appsClientSpecMapData
	const removeWidgetsFromAppsCSM = !!api.env.getExperiments()['specs.thunderbolt.removeWidgetsFromAppsCSM']

	const appsClientSpecMapData: TpaCommonsSiteConfig['appsClientSpecMapData'] = {}

	for (const app of Object.values(appsWithWidgets)) {
		const appData: TpaCommonsSiteConfig['appsClientSpecMapData'][string] = {
			applicationId: app.applicationId,
			appDefinitionName: app.appDefinitionName,
			appFields: app.appFields,
			isWixTPA: app.isWixTPA,
		}

		if (!removeWidgetsFromAppsCSM) {
			appData.widgets = app.widgets
		}

		appsClientSpecMapData[app.appDefinitionId] = appData
	}

	// Build tpaModalConfig (wixTPAs)
	const changeCSMKeysExperimentOn = !!api.env.getExperiments()['dm_changeCSMKeys']

	let clientSpecMapByAppDefId: Record<string, any>
	if (changeCSMKeysExperimentOn) {
		// CSM is already keyed by appDefinitionId
		clientSpecMapByAppDefId = clientSpecMap
	} else {
		// Remap to appDefinitionId
		clientSpecMapByAppDefId = {}
		for (const csm of Object.values(clientSpecMap)) {
			if ((csm as TPAPublicClientSpecEntry).appDefinitionId) {
				clientSpecMapByAppDefId[(csm as TPAPublicClientSpecEntry).appDefinitionId] = csm
			}
		}
	}

	const wixTPAs: Record<string, boolean> = {}
	for (const [key, csm] of Object.entries(clientSpecMapByAppDefId)) {
		if (csm.type === 'public' && csm.isWixTPA) {
			wixTPAs[key] = true
		}
	}

	const tpaModalConfig = { wixTPAs }

	// Parse appSectionParams
	const appSectionParamsStr = funcLib.getQueryParamValue(requestUrl, 'appSectionParams', { toLowerCase: false })
	const appSectionParams = appSectionParamsStr ? funcLib.parseJSON(appSectionParamsStr) : {}

	// Get routers config
	const routersConfig = api.rendererModel.getRouters()?.configMap ?? {}

	// Build routerByPrefix
	const routerByPrefix: Record<string, string> = {}
	for (const [routerId, routerConfig] of Object.entries(routersConfig)) {
		const prefix = routerConfig.prefix
		if (prefix) {
			routerByPrefix[prefix] = routerId
		}
	}

	// Build pageIdToPrefix
	const pageIdToPrefix: Record<string, string> = {}
	for (const routerConfig of Object.values(routersConfig)) {
		const prefix = routerConfig.prefix
		const pages = routerConfig.pages || {}
		for (const pageId of Object.values(pages)) {
			if (prefix && pageId) {
				pageIdToPrefix[pageId] = prefix
			}
		}
	}

	// Get other config fields
	const externalBaseUrl = api.publicModel.getExternalBaseUrl()
	const siteRevision = api.publicModel.getSiteRevision()
	const userFileDomainUrl = api.rootConfig.getServiceTopology()?.userFileDomainUrl ?? ''
	const metaSiteId = api.rendererModel.getMetaSiteId()
	const premiumFeatures = api.rendererModel.getPremiumFeatures() ?? []
	const isPremiumDomain = premiumFeatures.includes('HasDomain')
	const locale = api.rendererModel.getLanguageCode()
	const sitePropertiesInfo = api.rendererModel.getSitePropertiesInfo()
	const timeZone = sitePropertiesInfo?.timeZone as string
	const currency = sitePropertiesInfo?.currency
	const regionalLanguage = sitePropertiesInfo?.language as string

	// Calculate deviceType
	const isResponsive = api.rendererModel.getSiteMetaData()?.isResponsive ?? false
	const deviceViewMode = api.env.getViewMode()
	const deviceType = isResponsive ? (isMobileView ? 'mobile' : 'desktop') : deviceViewMode

	const debug = api.env.getMode().debug
	const isBuilderComponentModel = !!api.rendererModel.getIsBuilderComponentModel()

	const siteConfig: TpaCommonsSiteConfig = {
		widgetsClientSpecMapData,
		appsClientSpecMapData,
		previewMode: false,
		siteRevision,
		viewMode: 'site',
		editorOrSite: 'site',
		userFileDomainUrl,
		metaSiteId,
		isPremiumDomain,
		routersConfig,
		routerByPrefix,
		pageIdToPrefix,
		externalBaseUrl,
		tpaModalConfig,
		appSectionParams,
		requestUrl,
		isMobileView,
		isMobileDevice: deviceType === 'mobile',
		deviceType,
		extras: {
			currency,
		},
		tpaDebugParams,
		locale,
		timeZone,
		shouldRenderTPAsIframe: true,
		debug,
		regionalLanguage,
		isBuilderComponentModel,
	}

	return {
		shouldLoadSiteFeature: true,
		shouldLoadSiteConfig: true,
		siteConfig,
	}
}

const reactNativeTpaCommonsSiteConfig: SiteConfigFn<Record<string, never>> = ({ api }) => {
	const hasPlatformOnSite = api.features.isActive('platform')
	return {
		shouldLoadSiteFeature: hasPlatformOnSite,
		shouldLoadSiteConfig: hasPlatformOnSite,
		siteConfig: null,
	}
}

const name = 'tpaCommons'

export const config = { site: tpaCommonsSiteConfig, reactNativeSite: reactNativeTpaCommonsSiteConfig, name }
